import { formatDateKey, localDate, type OwnerTodoItem } from "@teamna/shared";
import type { Words } from "../families/model.ts";

/**
 * The words of the owner's To do list (concept D, strategy repo `teamna/prototypes/owner-answers.html`).
 * Pure, so each sentence is tested without a screen. The server sends the kind, the count, the first
 * names and the oldest date; the words come from here, in the business's own labels.
 */

export type TodoText = {
  title: string;
  detail: string;
  /** The button that opens the row. */
  action: string;
};

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "Mona Saeed", "Mona Saeed and Ali Hassan", "Mona Saeed, Ali Hassan and 4 more". */
export function namesText(item: OwnerTodoItem): string {
  const names = item.names;
  const more = item.count - names.length;
  if (names.length === 0) return "";
  if (more > 0) return `${names.join(", ")} and ${more} more`;
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "Waiting 3 days", or null on the first day. */
export function waitingText(since: string, now: Date): string | null {
  const days = Math.floor((now.getTime() - new Date(since).getTime()) / 86_400_000);
  if (days < 1) return null;
  return `Waiting ${plural(days, "day", "days")}`;
}

function sentence(...parts: Array<string | null>): string {
  return parts.filter((part): part is string => !!part).join(". ") + ".";
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** `timezone`: the business's, for the date of the last session. */
export function todoText(item: OwnerTodoItem, words: Words, now: Date, timezone = "Asia/Dubai"): TodoText {
  const names = namesText(item);
  const waiting = waitingText(item.since, now);
  const { count } = item;
  switch (item.kind) {
    case "parent_answers":
      return {
        title: `${plural(count, words.member, words.members)} answered the link`,
        detail: sentence(names || null, waiting),
        action: "Check",
      };
    case "pending_members":
      return {
        title: `${plural(count, words.member, words.members)} joined with your code`,
        detail: sentence(names || null, "Not on your list yet", waiting),
        action: "Check",
      };
    case "sessions_to_decide":
      return {
        title: `Decide ${plural(count, "session", "sessions")}`,
        detail: sentence(`${names || "They"} came and ${count === 1 ? "was" : "were"} not booked`, waiting),
        action: "Decide",
      };
    case "participants_to_confirm":
      return {
        title: `Confirm ${plural(count, `new ${words.participant}`, `new ${words.participants}`)}`,
        detail: sentence(names || null, `Added by a ${words.member}. Check the ${words.group}`, waiting),
        action: "Check",
      };
    case "plan_next_month": {
      // `since` is the last planned session; `month` the month to plan.
      const name = item.month ? MONTHS[Number(item.month.slice(5, 7)) - 1]! : "the next month";
      const lastDay = localDate(new Date(item.since), timezone);
      const ended = lastDay < localDate(now, timezone);
      return {
        title: `Plan ${name}`,
        detail: sentence(
          ended ? `The last session was on ${formatDateKey(lastDay)}` : `The last session is on ${formatDateKey(lastDay)}`,
          `${capital(words.members)} cannot book ${name} yet`,
        ),
        action: "Plan",
      };
    }
    case "cover_requests":
      // `names`: the people who said yes. Approve each cover is on.
      return {
        title: `Approve ${plural(count, "cover", "covers")}`,
        detail: sentence(names ? `${names} said yes` : null, `Until you approve, the ${words.staff} who asked stays on the session`, waiting),
        action: "Check",
      };
    case "fees_to_confirm":
      // A no-show or late-cancel fee (I2). `names`: the people the fees are for.
      return {
        title: `${plural(count, "fee", "fees")} to confirm`,
        detail: sentence(names || null, "Send each one with a card link, or waive it", waiting),
        action: "Check",
      };
    case "came_during_pause":
      // Came took a session from a paused package (2026-10-29). `names`: who came.
      return {
        title: `${plural(count, words.participant, words.participants)} came during a pause`,
        detail: sentence(names || null, "Came took 1 session. End the pause now?", waiting),
        action: "Check",
      };
    case "link_parents":
      return {
        title: `${plural(count, words.participant, words.participants)} with no ${words.member} yet`,
        detail: sentence(`Link each one to a ${words.member}. ${capital(words.members)} get no message until you invite them`),
        action: "Open",
      };
  }
}

/** "1 job", "4 jobs". */
export function jobsText(count: number): string {
  return plural(count, "job", "jobs");
}

function capital(word: string): string {
  return word ? word[0]!.toUpperCase() + word.slice(1) : word;
}
